import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Bell,
  Loader2,
  Settings,
  Clock,
  AlertTriangle,
  Save,
  Mail,
  Plus,
  X,
  Shield,
  Users,
  UserMinus,
  Send,
  Zap,
  Timer,
  RefreshCw
} from "lucide-react";

export default function AlertasVisitantesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    tiempo_alerta_minutos: 120,
    notificar_recepcion: true,
    notificar_seguridad: false,
    emails_notificacion: []
  });
  const [alertas, setAlertas] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [autoAlertStatus, setAutoAlertStatus] = useState({
    auto_alert_enabled: false,
    alert_interval_minutes: 30,
    task_running: false
  });
  const [togglingAuto, setTogglingAuto] = useState(false);
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, alertasRes, autoStatusRes] = await Promise.all([
        axios.get(`${API}/visitantes/alertas/config`, getAuthHeaders()),
        axios.get(`${API}/visitantes/alertas`, getAuthHeaders()),
        axios.get(`${API}/visitantes/alertas/auto/status`, getAuthHeaders()).catch(() => ({ data: { auto_alert_enabled: false } }))
      ]);
      // Merge API response with defaults to ensure all fields exist
      setConfig(prev => ({
        tiempo_alerta_minutos: 120,
        notificar_recepcion: true,
        notificar_seguridad: false,
        emails_notificacion: [],
        ...configRes.data
      }));
      setAlertas(alertasRes.data);
      setAutoAlertStatus(autoStatusRes.data);
    } catch (err) {
      toast.error("Error al cargar configuración de alertas");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/visitantes/alertas/config`, config, getAuthHeaders());
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoAlerts = async () => {
    if (config.emails_notificacion?.length === 0) {
      toast.error("Configure al menos un email de notificación antes de activar alertas automáticas");
      return;
    }
    
    try {
      setTogglingAuto(true);
      const response = await axios.post(`${API}/visitantes/alertas/auto/toggle`, {}, getAuthHeaders());
      setAutoAlertStatus(prev => ({
        ...prev,
        auto_alert_enabled: response.data.auto_alert_enabled
      }));
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar estado de alertas automáticas");
    } finally {
      setTogglingAuto(false);
    }
  };

  const handleEnviarNotificacion = async () => {
    if (!config.emails_notificacion || config.emails_notificacion.length === 0) {
      toast.error("Configure al menos un email de notificación");
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/visitantes/alertas/enviar-notificacion`, {}, getAuthHeaders());
      toast.success(`Notificación enviada a ${response.data.emails_enviados} email(s)`);
      if (response.data.errores?.length > 0) {
        toast.warning(`Algunos emails fallaron: ${response.data.errores.join(", ")}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar notificación");
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Ingrese un email válido");
      return;
    }
    const currentEmails = config.emails_notificacion || [];
    if (currentEmails.includes(newEmail)) {
      toast.error("Este email ya está en la lista");
      return;
    }
    setConfig({
      ...config,
      emails_notificacion: [...currentEmails, newEmail]
    });
    setNewEmail("");
  };

  const handleRemoveEmail = (email) => {
    const currentEmails = config.emails_notificacion || [];
    setConfig({
      ...config,
      emails_notificacion: currentEmails.filter(e => e !== email)
    });
  };

  const handleRegistrarSalida = async (visitanteId) => {
    try {
      await axios.post(`${API}/visitantes/${visitanteId}/salida`, {}, getAuthHeaders());
      toast.success("Salida registrada correctamente");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar salida");
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="alertas-visitantes-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Alertas de Visitantes
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Configuración y monitoreo de tiempo de visita
              </p>
            </div>
          </div>
          {alertas.total_alertas > 0 && (config.emails_notificacion?.length || 0) > 0 && (
            <Button 
              onClick={handleEnviarNotificacion}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="enviar-notificacion-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Notificación por Email
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Alertas
              </CardTitle>
              <CardDescription>
                Configure el tiempo máximo de visita y las notificaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Time threshold */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Tiempo de Alerta (minutos)
                </Label>
                <p className="text-sm text-slate-500">
                  Los visitantes que excedan este tiempo generarán una alerta
                </p>
                <Input
                  type="number"
                  value={config.tiempo_alerta_minutos}
                  onChange={(e) => setConfig({ ...config, tiempo_alerta_minutos: parseInt(e.target.value) || 120 })}
                  min={15}
                  max={480}
                  className="w-32"
                />
                <p className="text-xs text-slate-400">
                  Equivalente a {Math.floor(config.tiempo_alerta_minutos / 60)}h {config.tiempo_alerta_minutos % 60}m
                </p>
              </div>

              {/* Notification switches */}
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    <div>
                      <Label>Notificar a Recepción</Label>
                      <p className="text-xs text-slate-500">Mostrar alertas en el dashboard de recepción</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.notificar_recepcion}
                    onCheckedChange={(v) => setConfig({ ...config, notificar_recepcion: v })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-500" />
                    <div>
                      <Label>Notificar a Seguridad</Label>
                      <p className="text-xs text-slate-500">Enviar alertas al personal de seguridad</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.notificar_seguridad}
                    onCheckedChange={(v) => setConfig({ ...config, notificar_seguridad: v })}
                  />
                </div>
              </div>

              {/* Email notifications */}
              <div className="space-y-3 border-t pt-4">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Emails de Notificación
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  />
                  <Button variant="outline" onClick={handleAddEmail}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(config.emails_notificacion || []).map((email, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <button onClick={() => handleRemoveEmail(email)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Automatic Alerts */}
              <div className="space-y-4 border-t pt-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 -mx-6 px-6 py-4 -mb-6 rounded-b-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold">Alertas Automáticas</Label>
                      <p className="text-xs text-slate-500">
                        {autoAlertStatus.auto_alert_enabled 
                          ? `Enviando alertas cada ${autoAlertStatus.alert_interval_minutes} minutos` 
                          : "Las alertas se enviarán automáticamente cuando se active"}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleToggleAutoAlerts}
                    disabled={togglingAuto}
                    variant={autoAlertStatus.auto_alert_enabled ? "destructive" : "default"}
                    className={autoAlertStatus.auto_alert_enabled ? "" : "bg-indigo-600 hover:bg-indigo-700"}
                    data-testid="toggle-auto-alerts-btn"
                  >
                    {togglingAuto ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : autoAlertStatus.auto_alert_enabled ? (
                      <Timer className="w-4 h-4 mr-2" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2" />
                    )}
                    {autoAlertStatus.auto_alert_enabled ? "Desactivar" : "Activar"}
                  </Button>
                </div>
                
                {autoAlertStatus.auto_alert_enabled && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-green-700 dark:text-green-400">
                      Sistema de alertas automáticas activo
                    </span>
                    <Badge variant="outline" className="ml-auto text-xs">
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Cada {autoAlertStatus.alert_interval_minutes} min
                    </Badge>
                  </div>
                )}
              </div>

              <Button 
                onClick={handleSaveConfig} 
                disabled={saving}
                className="w-full bg-indigo-600 hover:bg-indigo-700 mt-4"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Configuración
              </Button>
            </CardContent>
          </Card>

          {/* Current Alerts */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Alertas Activas
                {alertas.total_alertas > 0 && (
                  <Badge className="bg-red-500 text-white ml-2">{alertas.total_alertas}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Visitantes que han excedido el tiempo límite ({alertas.tiempo_alerta_minutos || 120} minutos)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alertas.visitantes?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No hay alertas activas</p>
                  <p className="text-sm">Todos los visitantes están dentro del tiempo permitido</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alertas.visitantes?.map((v) => (
                    <div 
                      key={v.id} 
                      className="p-4 bg-red-50 rounded-lg border border-red-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-red-900">{v.nombre}</p>
                          <p className="text-sm text-red-700 font-mono">{v.cedula}</p>
                          {v.empresa && (
                            <p className="text-sm text-red-600">{v.empresa}</p>
                          )}
                          {v.persona_visita_nombre && (
                            <p className="text-sm text-red-600">Visita a: {v.persona_visita_nombre}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge className="bg-red-600 text-white">
                            {v.tiempo_transcurrido}
                          </Badge>
                          <p className="text-xs text-red-500 mt-1">
                            +{v.minutos_excedidos} min excedidos
                          </p>
                          <p className="text-xs text-red-400">
                            Entrada: {formatTime(v.fecha_entrada)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button 
                          size="sm"
                          onClick={() => handleRegistrarSalida(v.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <UserMinus className="w-4 h-4 mr-1" />
                          Registrar Salida
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
